import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React from 'react'
import {
  OnDemandCity,
  OnDemandCityConfig
} from 'src/constants/onDemandCoverage'
import { PillarPage } from 'src/constants/searchPages'

const PILLAR_BASE_HREF = `/${PillarPage.SelfStorage}`

interface IHoodLink {
  /** Already-constructed pillar URL — shapes differ between metros (MTY
   *  satellites use `/{pillar}/{citySlug}`, CDMX hoods nest under
   *  `/{pillar}/ciudad-de-mexico/{hoodSlug}`), so we let the data carry the
   *  full path rather than reconstruct it per-call. */
  href: string
  label: string
}

interface IPillarLinks {
  /** Href for the city-level pillar CTA (`/renta-mini-bodegas/{citySlug}`). */
  cityHref: string
  /** Curated list of hood / satellite-city links. Empty for cities where
   *  the SelfStorage routes don't have a meaningful sub-list to expose. */
  hoods: IHoodLink[]
}

/**
 * Curated SelfStorage cross-links for each on-demand market.
 *
 * Hand-picked rather than enumerated from the searchPages constants because:
 *  - MTY's satellite cities are top-level `SearchPageCity` slugs that route
 *    as `/{pillar}/{slug}`, while CDMX uses nested `MexicoCityHoods` routes
 *    `/{pillar}/ciudad-de-mexico/{hood}`. Routing shape differs between
 *    metros, so we emit hrefs verbatim.
 *  - We surface only the highest-traffic ~6 areas per city, not the full set,
 *    to keep the chip grid scannable and avoid overwhelming the user.
 *
 * Update this map when adding a new on-demand market or when search-volume
 * data shifts the popular hoods.
 */
/**
 * Curated top-cities chip grid for the parent variant of the cross-link
 * section. Mirrors the per-city hood grid but at the pillar level so a
 * user arriving at the generic parent page (`/renta-mini-bodegas/
 * almacenamiento-a-domicilio`) can jump straight to the
 * `/renta-mini-bodegas/{city}` pillar for their market without
 * scrolling back up.
 *
 * Hand-picked: 3 primary markets (MTY / CDMX / GDL) at the top, plus 6
 * popular secondary cities. Order tracks SpotMe coverage tiers from
 * CLAUDE.md. Update when a new city's pillar page launches or when
 * search-volume data shifts the popular set.
 */
const PARENT_CITY_LINKS: IHoodLink[] = [
  { href: `${PILLAR_BASE_HREF}/monterrey`, label: 'Monterrey' },
  { href: `${PILLAR_BASE_HREF}/ciudad-de-mexico`, label: 'Ciudad de México' },
  { href: `${PILLAR_BASE_HREF}/guadalajara`, label: 'Guadalajara' },
  { href: `${PILLAR_BASE_HREF}/queretaro`, label: 'Querétaro' },
  { href: `${PILLAR_BASE_HREF}/puebla`, label: 'Puebla' },
  { href: `${PILLAR_BASE_HREF}/tijuana`, label: 'Tijuana' },
  { href: `${PILLAR_BASE_HREF}/merida`, label: 'Mérida' },
  { href: `${PILLAR_BASE_HREF}/cancun`, label: 'Cancún' },
  { href: `${PILLAR_BASE_HREF}/leon`, label: 'León' }
]

const PILLAR_LINKS: Record<OnDemandCity, IPillarLinks> = {
  monterrey: {
    cityHref: `${PILLAR_BASE_HREF}/monterrey`,
    hoods: [
      {
        href: `${PILLAR_BASE_HREF}/san-pedro-garza-garcia`,
        label: 'San Pedro'
      },
      { href: `${PILLAR_BASE_HREF}/santa-catarina`, label: 'Santa Catarina' },
      { href: `${PILLAR_BASE_HREF}/guadalupe`, label: 'Guadalupe' },
      { href: `${PILLAR_BASE_HREF}/apodaca`, label: 'Apodaca' },
      { href: `${PILLAR_BASE_HREF}/general-escobedo`, label: 'Escobedo' },
      { href: `${PILLAR_BASE_HREF}/san-nicolas`, label: 'San Nicolás' }
    ]
  },
  cdmx: {
    cityHref: `${PILLAR_BASE_HREF}/ciudad-de-mexico`,
    hoods: [
      {
        href: `${PILLAR_BASE_HREF}/ciudad-de-mexico/polanco`,
        label: 'Polanco'
      },
      {
        href: `${PILLAR_BASE_HREF}/ciudad-de-mexico/condesa`,
        label: 'Condesa'
      },
      {
        href: `${PILLAR_BASE_HREF}/ciudad-de-mexico/roma-norte`,
        label: 'Roma'
      },
      {
        href: `${PILLAR_BASE_HREF}/ciudad-de-mexico/del-valle`,
        label: 'Del Valle'
      },
      {
        href: `${PILLAR_BASE_HREF}/ciudad-de-mexico/coyoacan`,
        label: 'Coyoacán'
      },
      {
        href: `${PILLAR_BASE_HREF}/ciudad-de-mexico/santa-fe`,
        label: 'Santa Fe'
      }
    ]
  },
  guadalajara: {
    cityHref: `${PILLAR_BASE_HREF}/guadalajara`,
    // Pre-wired alongside the on-demand coverage entry. Add Zapopan,
    // Tlaquepaque, Tonalá, etc. when the GDL on-demand market activates.
    hoods: []
  }
}

interface IProps {
  /** City-page variant. When set, renders the city pillar CTA + hood chip
   *  grid scoped to that city. Undefined on the parent page → single
   *  pillar-wide CTA pointing at `/renta-mini-bodegas`. */
  coverage?: OnDemandCityConfig
}

/**
 * Cross-link section at the bottom of the Almacenamiento a Domicilio
 * landing pages, offering an exit ramp to the traditional SelfStorage
 * pillar for users who'd rather rent a physical mini-bodega than use the
 * door-to-door service.
 *
 * Two variants:
 *  - **Parent page** (no coverage prop): single CTA → `/renta-mini-bodegas`.
 *    "Disponible en 15+ ciudades" framing widens the funnel for users who
 *    aren't tied to a specific city yet.
 *  - **City page** (coverage prop set): city-pillar CTA + curated grid of
 *    hood / satellite-city chips so users can drill straight to the area
 *    they care about. Hoods come from `PILLAR_LINKS[coverage.canonicalSlug]`
 *    and are hand-curated for routing-shape and search-volume reasons.
 *
 * SEO: an internal-link block this dense improves crawl coverage between
 * the on-demand and pillar trees and recovers some of the SEO juice that
 * would otherwise leak to users who bounce.
 *
 * Editorial vocabulary — cream-300 ground, hairline border, Fraunces
 * eyebrow + H2 with italic accent, blue arrow on the city CTA mirroring
 * the rest of the page's CTA cadence.
 */
const PillarCrossLink = ({ coverage }: IProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')

  const cityLinks = coverage ? PILLAR_LINKS[coverage.canonicalSlug] : null
  const isCity = !!coverage && !!cityLinks

  const title = isCity
    ? t('PILLAR_CROSS_LINK.TITLE_CITY', { city: coverage.displayShortName })
    : t('PILLAR_CROSS_LINK.TITLE')

  const ctaHref = isCity ? cityLinks.cityHref : PILLAR_BASE_HREF
  const ctaLabel = isCity
    ? t('PILLAR_CROSS_LINK.CTA_CITY', { city: coverage.displayShortName })
    : t('PILLAR_CROSS_LINK.CTA')

  return (
    <section
      aria-labelledby="pillar-cross-link-heading"
      className="border-t border-ink-100/15 bg-cream-300 py-20 md:py-28"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="grid grid-cols-1 items-start gap-10 md:grid-cols-12 md:gap-14">
          <div className="md:col-span-5">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {t('PILLAR_CROSS_LINK.EYEBROW')}
            </p>
            <h2
              id="pillar-cross-link-heading"
              className="mt-3 font-display text-[24px] leading-[1.2] font-medium tracking-[-0.01em] text-blue-500 italic md:text-[32px]"
            >
              {title}
            </h2>
            <p className="mt-3 max-w-md text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
              {t('PILLAR_CROSS_LINK.BODY')}
            </p>
          </div>

          <div className="flex flex-col gap-6 md:col-span-7">
            <Link
              href={ctaHref}
              className="group inline-flex w-fit items-center gap-2 rounded-full bg-blue-500 px-6 py-3 text-[15px] font-medium text-white-500 transition-transform duration-200 hover:translate-x-0.5"
            >
              <span>{ctaLabel}</span>
              <ArrowForwardRoundedIcon className="text-[16px] transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>

            {isCity && cityLinks.hoods.length > 0 && (
              <div className="flex flex-col gap-3 border-t border-ink-100/15 pt-5">
                <p className="font-display text-[11px] tracking-[0.18em] text-ink-200 uppercase">
                  {t('PILLAR_CROSS_LINK.HOODS_LABEL')}
                </p>
                <ul className="flex flex-wrap items-center gap-2">
                  {cityLinks.hoods.map((hood) => (
                    <li key={hood.href}>
                      <Link
                        href={hood.href}
                        className="inline-flex items-center rounded-full border border-ink-100/20 bg-white-500/80 px-4 py-1.5 text-[13px] font-medium text-ink-700 backdrop-blur-sm transition-colors hover:border-blue-500 hover:text-blue-500"
                      >
                        {hood.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!isCity && (
              <div className="flex flex-col gap-3 border-t border-ink-100/15 pt-5">
                <p className="font-display text-[11px] tracking-[0.18em] text-ink-200 uppercase">
                  {t('PILLAR_CROSS_LINK.CITIES_LABEL')}
                </p>
                <ul className="flex flex-wrap items-center gap-2">
                  {PARENT_CITY_LINKS.map((city) => (
                    <li key={city.href}>
                      <Link
                        href={city.href}
                        className="inline-flex items-center rounded-full border border-ink-100/20 bg-white-500/80 px-4 py-1.5 text-[13px] font-medium text-ink-700 backdrop-blur-sm transition-colors hover:border-blue-500 hover:text-blue-500"
                      >
                        {city.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default React.memo(PillarCrossLink)
